import React, { FC, useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// components
import Input from '../../../atoms/input';
// actions
import appAction from '../../../../redux/actions/app';
// types
import { authParamsType, stateType } from '../../../../types/types';

const CLEAR_LOGIN_FORM_KEY = 'clearLoginForm';

interface FormLoginProps {
  stateValues?: authParamsType;
}

const FormLogin: FC<FormLoginProps> = ({ stateValues }: FormLoginProps) => {
  const [values, setValues] = useState({
    email: '',
    password: '',
  });
  const [fieldsLocked, setFieldsLocked] = useState(!stateValues);

  const history = useHistory();
  const dispatch = useDispatch();

  // store
  const appLoading = useSelector((state: stateType) => state.app.loading);

  // Prefills credentials after registration; otherwise keeps fields empty until user focuses them.
  useEffect(() => {
    const shouldClear = sessionStorage.getItem(CLEAR_LOGIN_FORM_KEY) === '1';
    if (shouldClear) {
      sessionStorage.removeItem(CLEAR_LOGIN_FORM_KEY);
    }

    if (stateValues) {
      setValues({ email: stateValues.email, password: stateValues.password });
      setFieldsLocked(false);
      return;
    }

    setValues({ email: '', password: '' });
    setFieldsLocked(true);
  }, [stateValues]);

  const unlockFields = () => {
    setFieldsLocked(false);
  };

  // Unlocks fields on tap (iOS Safari ignores focus/click on readonly type=email inputs).
  const handleFieldClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    if (fieldsLocked) {
      setFieldsLocked(false);
      // Give React a tick to re-render without readOnly, then focus.
      setTimeout(() => {
        input.focus();
        // Move cursor to end.
        const len = input.value.length;
        input.setSelectionRange(len, len);
      }, 50);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'email') {
      setValues((prev) => ({ ...prev, email: value }));
      return;
    }
    if (name === 'password') {
      setValues((prev) => ({ ...prev, password: value }));
    }
  };

  const doctorCallback = () => {
    history.push('/doctor_home');
  };

  const patientCallback = () => {
    history.push('/calibration');
  };

  const handleQr = () => {
    history.push('/qr_scan');
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (fieldsLocked) {
      setFieldsLocked(false);
    }

    form.querySelectorAll('input[readonly]').forEach((node) => {
      (node as HTMLInputElement).readOnly = false;
    });

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    dispatch(appAction.login(values, patientCallback, doctorCallback));
  };

  return (
    <>
      <form className="form" onSubmit={handleSubmit} autoComplete="on">
        <div className="form__heading">
          <h3 className="page-heading small">Login</h3>
          <span className="page-description light">
            A Dyop is a spinning segmented visual target where the ability to detect the direction of spinning of the
            smallest diameter (arc width) Dyop is an indicator (benchmark) of visual clarity/acuity.
          </span>
        </div>
        <div className="form__row">
          <div className="form__column">
            <Input
              label="Email"
              placeholder="Email"
              input={values.email}
              id="email"
              name="email"
              disabled={appLoading.status}
              handleChange={handleInput}
              inputType="email"
              icon="email"
              required={true}
              autoComplete="username"
              onFocus={unlockFields}
            />
          </div>
        </div>
        <div className="form__row">
          <div className="form__column">
            <Input
              label="Password"
              placeholder="Password"
              input={values.password}
              id="password"
              name="password"
              disabled={appLoading.status}
              handleChange={handleInput}
              icon="password"
              inputType="password"
              required={true}
              min={6}
              readOnly={fieldsLocked}
              autoComplete="current-password"
              onFocus={unlockFields}
              onClick={handleFieldClick}
            />
          </div>
          <Link className="form__forgot-link" to="/forgot-password">
            Forgot password?
          </Link>
        </div>
        <div className="form__btns form__btns_login">
          <div>
            <span>Don`t have an account?</span>
            <Link to="/registration">Register</Link>
          </div>
          <div className="form__btns-item">
            <button type="button" className="btn btn_submit btn_wide" disabled={appLoading.status} onClick={handleQr}>
              Scan QR code
            </button>
            <button
              type="submit"
              className={`btn btn_submit btn_wide${appLoading.status ? ' btn_loading' : ''}`}
              disabled={appLoading.status}
              aria-busy={appLoading.status}
            >
              <span className="btn__label">Login</span>
              {appLoading.status ? <span className="btn__spinner" aria-hidden="true" /> : null}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default FormLogin;
